import React, { useMemo } from 'react';
import { getThemeColor } from '../utils/styles';
import styled, { css } from 'styled-components';

import { Box, Flex, Grid } from './UIKit/system';
import logoBoxImage from '../images/logo-box.png';
import Container from './container';
import Link from './link';
import mq from '../utils/mq';
import { useFooter } from '../hooks/use-footer';
import parseStoryblokLink from '../utils/parse-storyblok-link';
import { useSocialLinks } from '../hooks/use-social-links';
import { useContactData } from '../hooks/use-contact-data';
import CE from '../images/svg/CE.svg';
import UKCA from '../images/svg/UKCA.svg';
import ISO from '../images/svg/ISO.svg';

const StyledFooter = styled(Box)``;

const OuterContent = styled(Flex)`
  justify-content: space-between;

  ${mq.tablet(css`
    flex-direction: column;
    justify-content: center;
  `)};
`;

const LinkContainerOuter = styled(Box)`
  ${mq.tablet(css`
    width: 100%;
  `)};
`;

const LogoContainerOuter = styled(Box)`
  ${mq.tablet(css`
    width: 100%;
    display: flex;
    justify-content: start;
    align-items: center;
    margin-bottom: 40px;
  `)};
`;

const LogoImage = styled.img`
  width: 110px;

  ${mq.mobile(css`
    display: block;
  `)};
`;

const LinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 792px;
`;

const RowCollection = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -40px;
`;

const LinkCollection = styled.div`
  margin-bottom: 40px;
  width: 25%;

  ${mq.mobile(css`
    width: 50%;
  `)}

  ${(props) =>
    props.twoColumns &&
    css`
      width: 50%;

      ${mq.mobile(css`
        width: 100%;
      `)}
    `}
`;

const LinkTitle = styled.div`
  font-weight: bold;
  font-size: 15px;
  line-height: normal;
  color: var(--Color-Purple-4, #5616ca);
  height: 40px;
  letter-spacing: 0;
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  ${(props) =>
    props.twoColumns &&
    css`
      flex-direction: row;

      > * {
        width: 50%;
      }
    `};

  ${mq.tablet(css`
    width: 100%;
  `)};
`;

const StyledLink = styled(Link)`
  font-size: 14px;
  color: var(--Foreground-fg-secondary, #565656);
  line-height: normal;
  padding-top: 7.5px;
  padding-bottom: 7.5px;
  cursor: pointer;
  transition: 300ms;
  text-decoration: none;
  letter-spacing: 0;

  :hover {
    transition: 300ms;
    color: ${getThemeColor('purple')};
  }
`;

const StyledLinkItem = styled.div`
  display: flex;
`;

function LinkItem(props) {
  return (
    <StyledLinkItem>
      <StyledLink {...props} />
    </StyledLinkItem>
  );
}

const LinkItemLocation = styled.div`
  font-size: 14px;
  line-height: 18px;
  color: #666666;
  padding-top: 28px;
`;

const LinkItemPhone = styled.a`
  display: inline-flex;
  font-size: 14px;
  line-height: 18px;
  color: #666666;
  padding-top: 14px;
  transition: 300ms;
  cursor: pointer;
  :hover {
    transition: 300ms;
    color: ${getThemeColor('purple')};
  }
`;

const CopyrightDivider = styled.div`
  border: 0px;
  border-top: 1px solid var(--Border-border-primary, #cbc9c9);
  margin-top: 50px;
  margin-bottom: 25px;
  ${mq.mobile(css`
    margin-top: 35px;
  `)};
`;

const CopyrightText = styled.div`
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  color: #666666;
  letter-spacing: 0;
`;

const SocialIconsContainer = styled.div`
  display: flex;
  width: 46px;
  justify-content: space-between;
  margin-top: 15px;

  ${mq.tablet(css`
    margin-top: 10px;
  `)}
`;

const SocialIcon = styled.div`
  width: 17px;
  height: 16px;
`;

const MobileLinkItem = styled(Link)`
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  color: #666666;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const StyledBottomLeft = styled(Grid).attrs(() => ({
  gridAutoFlow: [undefined, 'column'],
  gridColumnGap: 32,
  gridRowGap: 0,
  alignItems: 'center',
  justifyContent: 'start',
  marginBottom: 0
}))`
  ${mq.mobile(css`
    display: flex;
    flex-wrap: wrap;
  `)}
`;

const StyledBottomRight = styled(Grid).attrs(() => ({
  gridAutoFlow: 'column',
  alignItems: 'center',
  justifyContent: ['space-between', 'space-between', 'end'],
  gridGap: 40,
  marginBottom: 0
}))``;

interface FooterProps {
  footerEditorContent?: {
    columns: {
      _uid: string;
      title: string;
      links: {
        _uid: string;
        name: string;
        link: Record<string, any>;
      }[];
      twoColumns?: boolean;
      contactDetails?: boolean;
    }[];
    bottomLinks?: {
      _uid: string;
      name: string;
      link: Record<string, any>;
    }[];
    copyright: string;
  };
}

function Footer({ footerEditorContent }: FooterProps) {
  const footerCmsData: NonNullable<FooterProps['footerEditorContent']> =
    useFooter();
  const footerData = footerEditorContent || footerCmsData;
  const socialLinksData = useSocialLinks();
  const contactData = useContactData();
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <StyledFooter pt={[32, 60, 75]} pb={25}>
      <Container large>
        <OuterContent>
          <LogoContainerOuter>
            <LogoImage src={logoBoxImage} />
          </LogoContainerOuter>
          <LinkContainerOuter>
            <LinkContainer>
              <RowCollection>
                {footerData.columns.map((column) => {
                  return (
                    <LinkCollection
                      key={column._uid}
                      twoColumns={column.twoColumns}
                    >
                      <LinkTitle>
                        {!column.contactDetails && column.title}
                      </LinkTitle>
                      <Links twoColumns={column.twoColumns}>
                        {column.links &&
                          column.links.map((item) => {
                            return (
                              <LinkItem
                                key={item._uid}
                                to={parseStoryblokLink(item.link)}
                              >
                                {item.name}
                              </LinkItem>
                            );
                          })}
                      </Links>
                      {column.socialLinks && socialLinksData && (
                        <SocialIconsContainer>
                          {socialLinksData.items.map((item) => {
                            return (
                              <SocialIcon key={item._uid}>
                                <Link
                                  to={parseStoryblokLink(item.link)}
                                  target="_blank"
                                >
                                  {item.icon && (
                                    <img
                                      src={item.icon.filename}
                                      aria-hidden="true"
                                      alt="Icon"
                                    />
                                  )}
                                </Link>
                              </SocialIcon>
                            );
                          })}
                        </SocialIconsContainer>
                      )}
                      {column.contactDetails && (
                        <div>
                          {contactData.phone && (
                            <LinkItemPhone href={`tel:${contactData.phone}`}>
                              {contactData.phone}
                            </LinkItemPhone>
                          )}
                          {contactData.address && (
                            <LinkItemLocation>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: contactData.address
                                }}
                              />
                            </LinkItemLocation>
                          )}
                        </div>
                      )}
                    </LinkCollection>
                  );
                })}
              </RowCollection>
            </LinkContainer>
          </LinkContainerOuter>
        </OuterContent>
      </Container>
      <CopyrightDivider />
      <Container large>
        <Grid gridAutoFlow={['row', 'row', 'column']} gridGap={[30, 15]}>
          <StyledBottomLeft>
            {footerData.bottomLinks?.map((link) => (
              <MobileLinkItem
                key={link._uid}
                to={parseStoryblokLink(link.link)}
              >
                {link.name}
              </MobileLinkItem>
            ))}
          </StyledBottomLeft>
          <StyledBottomRight>
            <Grid gridAutoFlow="column" gridGap={24}>
              <CE />
              <UKCA />
              <ISO />
            </Grid>
            {footerData.copyright && (
              <CopyrightText>
                &copy;{' '}
                {footerData.copyright.replace(
                  '{{ CURRENT_YEAR }}',
                  currentYear.toString()
                )}
              </CopyrightText>
            )}
          </StyledBottomRight>
        </Grid>
      </Container>
    </StyledFooter>
  );
}

export default Footer;
